import { SUPPLIER_FIELDS } from '@norum/contracts';
import type { WireBuyer, WireOffer, WireOfferRow, WireSupplier, WireSupplierCandidate, WireSupplierRow } from '@/api/wire';
import { nameOf, plural, withRole } from '@/outreach/explain';
import type { ItemContext, Party, StepAction } from '@/ui/context';
import { when } from '@/ui/records';
import type { Who } from '@/ui/who';
import { countryName, stateWords } from '@/ui/words';

/**
 * What each market-validation record says about itself (ADR-0155): the
 * buyer, the supplier and the offer that rests on both.
 *
 * Pure, like every builder: the row the screen holds and a `Who`. The rule
 * the offer follows is the platform's own (ADR-0135, Q18): it cannot be
 * prepared while any line rests on a supplier answer that is not in
 * writing, so the panel names which answer is missing rather than saying
 * "something is blocking it". The platform sends none of it: a question to
 * a supplier and an offer to a buyer are a person's email, from their own
 * address.
 */

const fieldLabel = (key: string): string => SUPPLIER_FIELDS.find((f) => f.key === key)?.label.toLowerCase() ?? key.replace(/_/g, ' ');
const dayOf = (iso: string | null | undefined): string | null =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : null;
/** "a, b and 3 more" — a list short enough to read in a sentence. */
const few = (items: string[], max = 4): string => (items.length <= max ? items.join(', ') : `${items.slice(0, max).join(', ')} and ${items.length - max} more`);
const leadOf = (who: Who, department: string): Party | null => who.lead(department);
const leadName = (who: Who, department: string): string => {
  const l = leadOf(who, department);
  return l && l.name !== 'You' ? l.name : 'the department’s lead';
};

export interface ValidationCtx {
  department: string;
  /** The department's lead, or a superadmin: who approves and prepares here. */
  mayDecide: boolean;
  /** The reader, so a record they brought names them "You". */
  humanId?: string | undefined;
}

/**
 * The person whose press added a record (0162): `undefined` on an older
 * reply without the field, so the row is left out; `null` when nobody was
 * recorded, which reads as not recorded. Never guessed.
 */
function addedBy(name: string | null | undefined, humanId: string | null | undefined, ctx: ValidationCtx, role: string, at?: string | null): ItemContext['broughtBy'] {
  if (name === undefined) return undefined;
  if (!name) return null;
  const mine = Boolean(humanId) && humanId === ctx.humanId;
  return { name: mine ? 'You' : name, kind: 'person', role, ...(at ? { when: at } : {}) };
}

// --- Buyers ------------------------------------------------------------------

export function explainBuyer(
  b: WireBuyer,
  who: Who,
  ctx: ValidationCtx & { approveCompany?: () => void; approveBuyer?: () => void; busy?: boolean },
): ItemContext {
  const relationship = b.company?.relationship ?? 'unapproved';
  const companyApproved = relationship !== 'unapproved';
  const reach = [b.linkedin && 'LinkedIn', b.phone && 'a phone number', b.email && 'an email address', b.switchboard && 'a switchboard'].filter(Boolean) as string[];
  const lead = leadOf(who, ctx.department);
  const title = b.erased ? `${b.companyName} (erased)` : `${b.companyName}, ${b.roleTitle}`;
  const prospect: StepAction[] = b.prospectId ? [{ label: 'Open the restaurant it came from', to: `/prospects/${b.prospectId}` }] : [];

  let next: ItemContext['next'];
  if (b.erased) {
    next = { tip: 'Nothing to do. Their details were destroyed on request; only the fact that a buyer existed at this company remains.', instead: 'If you need a contact at the company, add a new buyer you find.' };
  } else if (b.approved) {
    next = {
      tip: 'Nothing is needed. They are approved, so a person may write to them from their own address; the platform sends nothing. Use them as the recipient of an offer once one is ready.',
      instead: reach.length === 0 ? 'No way to reach them is on record yet: add one before an offer can be addressed to them.' : undefined,
      actions: [{ label: 'See the offers', to: '/offers' }, ...prospect],
    };
  } else if (!ctx.mayDecide) {
    next = {
      tip: `Nothing is needed from you. ${leadName(who, ctx.department)} approves buyers before anyone writes to them.`,
      instead: b.confidence === 'low' ? 'Confidence is low: if you know their role to be right or wrong, tell the lead before they decide.' : 'If you know more about them, such as a direct line or their real role, tell the lead.',
      actions: prospect,
    };
  } else if (!companyApproved && (b.company || b.prospectId)) {
    next = {
      tip: 'Approve the company first, under a single-use token; then approve this buyer. Nobody is written to at a company that is not approved.',
      instead: 'If the company is not one we want yet, leave both: nothing is written to an unapproved buyer.',
      actions: [...(ctx.approveCompany ? [{ label: 'Approve the company…', run: ctx.approveCompany, primary: true }] : []), ...prospect],
    };
  } else if (!companyApproved) {
    next = {
      tip: 'Approve the company on Companies first, under a single-use token; then come back to approve this buyer.',
      instead: 'It has no company on file and no restaurant behind it, so there is nothing here to approve yet.',
      actions: [{ label: 'Go to Companies', to: '/companies', primary: true }],
    };
  } else {
    next = {
      tip: 'Approve them: then they may be written to, by a person from their own address. The platform sends nothing.',
      instead: b.confidence === 'low' ? 'Confidence is low: confirm the role first if you can.' : 'Or leave them unapproved until you have a better way to reach them.',
      actions: [...(ctx.approveBuyer ? [{ label: 'Approve them', run: ctx.approveBuyer, busy: ctx.busy, primary: true }] : []), ...prospect],
    };
  }

  return {
    subject: { kind: 'buyer', title },
    summary: b.erased
      ? `A buyer at ${b.companyName} whose details were destroyed on request.`
      : b.persona
        ? `A purchasing role at ${b.companyName}${b.companyType ? ` (${b.companyType.toLowerCase()})` : ''}: ${b.roleTitle}. No named person is on record, only the role.`
        : `${b.name ?? 'A named buyer'} is ${b.roleTitle} at ${b.companyName}${b.companyType ? ` (${b.companyType.toLowerCase()})` : ''}, in ${countryName(b.marketCode)}. Reach: ${reach.length > 0 ? reach.join(', ') : 'nothing on record yet'}.`,
    need: b.erased ? 'Nothing.' : b.approved ? 'Nothing: approved.' : companyApproved ? 'The lead’s approval of this buyer.' : 'The company approved first, under a token, then this buyer.',
    why: 'The validation asks for the customer database: one purchasing contact per company, wholesalers first, because they also tell us who supplies the chains today.',
    source: { label: b.source },
    broughtBy: addedBy(b.addedBy, b.addedByHuman, ctx, 'added them'),
    owner: b.approved ? undefined : withRole(lead, 'approves buyers'),
    knows: who.knowers(ctx.department),
    facts: [
      ['Country', countryName(b.marketCode)],
      ['Company', stateWords(relationship)],
      ['Confidence', b.confidence.charAt(0).toUpperCase() + b.confidence.slice(1)],
      ['Supplied today by', b.suppliedBy],
    ],
    next,
    open: { to: `/buyers/${b.id}` },
  };
}

// --- Suppliers ---------------------------------------------------------------

/** The row a supplier's own page holds, in the shape the list holds. */
export function supplierRowOf(x: WireSupplier): WireSupplierRow {
  const { fields, ...rest } = x;
  delete (rest as { contact?: unknown }).contact;
  return { ...rest, answered: fields.filter((f) => f.answer).length, confirmed: fields.filter((f) => f.confirmed && !f.expired).length };
}

export function explainSupplier(x: WireSupplierRow, who: Who, ctx: ValidationCtx & { onPage?: boolean }): ItemContext {
  const total = SUPPLIER_FIELDS.length;
  const unanswered = total - x.answered;
  const notInWriting = total - x.confirmed;
  const step = x.nextStep ?? 'ask';
  // An older reply may not carry the two lists; the counts still say what is open.
  const missing = (x.missing ?? []).map(fieldLabel);
  const unconfirmed = (x.unconfirmed ?? []).map(fieldLabel);
  // From a list the panel's own "Open the full record" goes to the page; on the page there is nothing to open.
  const link: StepAction[] = [];
  const asked = x.askedAt ? `They were last asked on ${when(x.askedAt)}${x.askedBy ? ` by ${nameOf(who, x.askedBy) ?? x.askedBy}` : ''}.` : 'They have not been asked yet.';

  let next: ItemContext['next'];
  if (step === 'add_contact') {
    next = {
      tip: 'Add a contact first: nobody can be asked until there is a role address or a name to write to. On its page, add one, or press “Ask Otis to find one”; the agent reads their site and the register and writes to nobody.',
      instead: 'If you already know who to write to, adding them yourself is quicker.',
      actions: link,
    };
  } else if (step === 'ask') {
    next = x.askedAt
      ? {
          tip: `${unanswered} of ${total} questions are still unanswered${missing.length > 0 ? `: ${few(missing)}` : ''}. ${asked} If nothing has come back, open its page, press “Ask them for what’s missing”, change the draft on your day, send it from your own address and mark “I sent it”.`,
          instead: 'If they have answered by phone or in a message, record their answer on the page instead and confirm it in writing.',
          actions: link,
        }
      : {
          tip: `${unanswered} of ${total} questions are unanswered${missing.length > 0 ? `: ${few(missing)}` : ''}. Open its page and press “Ask them for what’s missing”: a draft lands on your day; change it as you like, send it from your own address, then mark “I sent it”.`,
          instead: 'The platform writes the draft and never sends it. If you already have their answers, record them on the page.',
          actions: link,
        };
  } else if (step === 'renew_price_list') {
    next = {
      tip: `Their price list${x.priceListValidUntil ? ` ran out on ${dayOf(x.priceListValidUntil)}` : ' has expired'}, so the answers that rest on it no longer count. Ask for a current list, then record it.`,
      instead: 'Offers that rest on the old prices stay blocked until the new answers are in writing.',
      actions: link,
    };
  } else if (step === 'confirm') {
    next = {
      tip: `Every question has an answer, but ${plural(notInWriting, 'answer is', 'answers are')} only said, not in writing${unconfirmed.length > 0 ? `: ${few(unconfirmed)}` : ''}. On its page, confirm each from their price list on file or from a conversation on file.`,
      instead: 'If it is only in a phone call, ask them to put it in an email; then confirm it from that conversation.',
      actions: link,
    };
  } else {
    next = {
      tip: 'Nothing to do: every answer is in and in writing, and the price list is current. An offer that rests on them can be prepared.',
      instead: 'It stays ready until the price list’s date passes; then its price answers stop counting the day after.',
      actions: link,
    };
  }

  return {
    subject: { kind: 'supplier', title: x.name },
    summary: `${x.name} (${x.country}) is ${x.role === 'current' ? 'our current manufacturer' : 'an alternative supplier'}. Of the ${total} questions a first order needs answered, ${x.answered} are answered and ${x.confirmed} are confirmed in writing.${x.priceListExpired ? ' Its price list has expired.' : ''}`,
    need:
      step === 'ready'
        ? 'Nothing: all answered and in writing.'
        : step === 'add_contact'
          ? 'A contact to write to.'
          : step === 'renew_price_list'
            ? 'A current price list.'
            : step === 'confirm'
              ? `${plural(notInWriting, 'answer')} confirmed in writing.`
              : `${plural(unanswered, 'answer')} from the supplier.`,
    why: 'Every line of a commercial offer rests on one of these answers, and an offer cannot go to a customer until each is answered, and answered in writing.',
    source: x.priceListDocument ? { label: `Their price list on file, “${x.priceListDocument}”`, when: x.asOf ? (dayOf(x.asOf) ?? undefined) : undefined } : null,
    // A maker Nia found was brought by her search; any other, by the person who added it.
    broughtBy: x.foundBySearch ? { name: 'Nia', kind: 'agent', role: 'found it on a search' } : addedBy(x.addedBy, x.addedByHuman, ctx, 'added it'),
    owner: leadOf(who, ctx.department),
    knows: who.knowers(ctx.department),
    facts: [
      ['Questions answered', `${x.answered} of ${total}`],
      ['In writing', `${x.confirmed} of ${total}`],
      ['Last asked', x.askedAt ? `${when(x.askedAt)}${x.askedBy ? `, by ${nameOf(who, x.askedBy) ?? x.askedBy}` : ''}` : 'Never'],
      ['Price list', x.priceListValidUntil ? `${x.priceListExpired ? 'Expired' : 'Valid until'} ${dayOf(x.priceListValidUntil)}` : null],
      ['Order minimum', x.orderMinimum],
    ],
    next,
    ...(ctx.onPage ? {} : { open: { to: `/suppliers/${x.id}` } }),
  };
}

// --- Manufacturers found (ADR-0162) ---------------------------------------------

export interface CandidateActions {
  shortlist: () => void;
  unshortlist: () => void;
  reject: () => void;
  main: () => void;
  backup: () => void;
  release: () => void;
  prepare: () => void;
  sent: () => void;
  addContact: () => void;
  busy?: boolean;
}

const hostOf = (url: string | null): string | null => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : null;
  } catch {
    return null;
  }
};

/**
 * A manufacturer on the way to being chosen: found on a page, read by a
 * person, shortlisted, written to, and at the end the main or the backup.
 * Says which of those it is and what a person does next. The platform
 * writes to no manufacturer: the email is drafted in the sender's own Gmail
 * and sent from there.
 */
export function explainCandidate(
  c: WireSupplierCandidate,
  who: Who,
  ctx: ValidationCtx & { compared: number; minCompared: number; actions: CandidateActions },
): ItemContext {
  const a = ctx.actions;
  const total = SUPPLIER_FIELDS.length;
  const host = hostOf(c.sourceUrl);
  const byNia = c.foundByRequest !== null;
  const contactWords = c.hasContact ? (c.contactKind === 'role' ? 'a shared sales address' : 'a named person') : null;
  const chooseNeeds = ctx.minCompared - ctx.compared;
  const act = (label: string, run: () => void, primary = false): StepAction => ({ label, run, busy: a.busy ?? false, primary });
  const actions: StepAction[] = [];
  let tip: string;
  let instead: string | undefined;
  let need: string | undefined;

  if (c.selection) {
    tip = `It is the chosen ${c.selection}. Nothing is needed unless you change your mind; releasing it frees the place for another.`;
    need = `Nothing: it is the ${c.selection} manufacturer.`;
    actions.push(act('Release it…', a.release));
  } else if (c.stage === 'rejected') {
    tip = 'It was rejected, with a reason kept on its page. Nothing to do.';
    need = 'Nothing.';
  } else if (c.stage === 'candidate' || c.stage === 'screened') {
    need = 'A person to read it, against the pages it was found on.';
    tip = `Open its page and check what was read from ${host ?? 'its site'} against the page itself: each answer is said, not confirmed. Then shortlist it, or reject it and say why.`;
    instead = 'If it is not a manufacturer, or cannot pack for a brand, reject it now so it is not compared.';
    actions.push(act('Shortlist it', a.shortlist, true), act('Reject it…', a.reject));
  } else if (c.stage === 'shortlisted' && !c.hasContact) {
    need = 'Someone to write to: a sales or partnerships contact, from a page.';
    tip = 'Add the contact from the page it is written on. Without one there is nobody to send the email to.';
    instead = 'If there is no contact on their site, ask for one through its contact form, then add it here.';
    actions.push(act('Add a contact…', a.addContact, true), act('Take it off the shortlist', a.unshortlist), act('Reject it…', a.reject));
  } else if (c.stage === 'shortlisted') {
    need = 'To be written to, so its commercial details can be confirmed.';
    tip = 'Press “Prepare the email”: your twin puts the standard email in your own Gmail drafts, to the contact on this page. Read it there, send it yourself, then press “I sent it”.';
    instead = 'The platform never sends it. If you would rather write it yourself, do, and press “I sent it”.';
    actions.push(act('Prepare the email', a.prepare, true), act('I sent it', a.sent), act('Add a different contact…', a.addContact), act('Reject it…', a.reject));
  } else {
    need = 'Its answer, and the price, lead time and trial batch in writing.';
    tip = 'Wait for their reply. When it comes, record what they said on its page and confirm each answer from their email or price list.';
    instead = 'If they do not answer in two weeks, write again from your own address, or reject it.';
    actions.push(act('Reject it…', a.reject));
  }
  if (!c.selection && (c.stage === 'shortlisted' || c.stage === 'contacted')) {
    if (chooseNeeds > 0) {
      tip += ` A main manufacturer is chosen from at least ${ctx.minCompared} compared: ${ctx.compared} on the shortlist now, so shortlist ${chooseNeeds} more first.`;
    } else {
      actions.push(act('Choose as main…', a.main), act('Choose as backup…', a.backup));
    }
  }

  return {
    subject: { kind: 'manufacturer', title: c.name },
    summary: `${c.name} (${countryName(c.country)}) is ${byNia ? 'a manufacturer Nia found on a search and read from its own site' : 'a manufacturer a person added from a page'}. ${c.answered} of the ${total} questions have an answer read from its pages.${contactWords ? ` It has ${contactWords} to write to.` : ' It has no contact yet.'}`,
    need,
    why: 'Of the manufacturers found, at least three are compared and one is chosen as the main and one as the backup for the Swedish market test.',
    source: host ? { label: `Its own site, ${host}` } : null,
    broughtBy: byNia ? { name: 'Nia', kind: 'agent', when: when(c.createdAt) } : addedBy(c.addedBy, c.addedByHuman, ctx, 'added it from a page', when(c.createdAt)),
    owner: leadOf(who, ctx.department),
    knows: who.knowers(ctx.department),
    facts: [
      ['Stage', { candidate: 'Found, not yet read', screened: 'On the list', shortlisted: 'Shortlisted', contacted: 'Written to', rejected: 'Rejected' }[c.stage]],
      ['Chosen as', c.selection ? (c.selection === 'main' ? 'The main manufacturer' : 'The backup') : null],
      ['Answers read', `${c.answered} of ${total}`],
      ['Contact', contactWords],
      ['Found on', host],
    ],
    next: { tip, ...(instead ? { instead } : {}), actions },
    open: { to: `/suppliers/${c.id}`, label: 'Open its page' },
  };
}

// --- Offers ------------------------------------------------------------------

/** The row an offer's own page holds, in the shape the list holds. */
export function offerRowOf(o: WireOffer): WireOfferRow {
  const { lines, ...rest } = o;
  return { ...rest, lineCount: lines.length };
}

const OFFER_STATE_WORDS = { draft: 'a draft', prepared: 'ready to send', sent: 'sent', withdrawn: 'withdrawn' } as const;

export function explainOffer(o: WireOfferRow, who: Who, ctx: ValidationCtx & { onPage?: boolean }): ItemContext {
  const blocked = o.blocking.length;
  const missing = [...new Set(o.blocking.map((b) => fieldLabel(b.field)))];
  const lead = leadOf(who, ctx.department);
  const recipients = o.recipients ?? [];
  const sentTo = recipients.filter((r) => r.sentAt).length;
  const supplierLink: StepAction[] = [{ label: `Open ${o.supplierName}`, to: `/suppliers/${o.supplierId}`, primary: true }];
  const link: StepAction[] = [];
  const drafter: Party | null = o.draftedBy
    ? o.draftedBy.name.includes('@')
      ? who.actor(o.draftedBy.name)
      : { name: o.draftedBy.name, kind: o.draftedBy.kind === 'agent' ? 'agent' : 'person' }
    : null;

  let next: ItemContext['next'];
  if (o.state === 'draft' && blocked > 0) {
    next = ctx.mayDecide
      ? {
          tip: `It cannot be prepared yet: ${plural(blocked, 'line rests', 'lines rest')} on supplier answers not in writing (${few(missing)}). Get those confirmed on ${o.supplierName}’s page; then the offer can be prepared.`,
          instead: 'Nothing in the offer needs changing: it is the supplier’s answers that hold it, and pressing the button now would be refused.',
          actions: supplierLink,
        }
      : {
          tip: `Nothing is needed from you, but you can help: ${plural(blocked, 'line rests', 'lines rest')} on answers ${o.supplierName} has not put in writing (${few(missing)}). If you have their written answer, record it on their page.`,
          instead: `${leadName(who, ctx.department)} prepares the offer once every line is confirmed.`,
          actions: supplierLink,
        };
  } else if (o.state === 'draft') {
    next = ctx.mayDecide
      ? {
          tip: 'Every line is confirmed. Open it and press “Send the offer”: that only prepares its text on your day, addressed to a buyer you choose; you send it from your own address and then say so here.',
          instead: 'If a buyer is not approved yet, approve them on Buyers first; an offer goes only to an approved buyer.',
          actions: link,
        }
      : { tip: `Nothing is needed from you. Every line is confirmed; ${leadName(who, ctx.department)} prepares it.`, actions: link };
  } else if (o.state === 'prepared') {
    next = ctx.mayDecide
      ? {
          tip: `Its text is on your day. Send it from your own address to the buyer, then open the offer and press “I have sent it” for each recipient${recipients.length > 0 ? ` (${sentTo} of ${recipients.length} marked so far)` : ''}.`,
          instead: 'The platform sends nothing; if you do not send it, it stays prepared.',
          actions: link,
        }
      : { tip: `Nothing is needed from you. Its text is on ${leadName(who, ctx.department)}’s day, to send from their own address.`, actions: link };
  } else if (o.state === 'sent') {
    next = {
      tip: `Nothing to do. It was sent${o.sentAt ? ` on ${when(o.sentAt)}` : ''}${recipients.length > 0 ? `, to ${plural(sentTo, 'buyer')} of ${recipients.length} addressed` : ''}. Replies arrive in Mail for a person to answer.`,
      instead: recipients.length > sentTo ? `${plural(recipients.length - sentTo, 'recipient')} still marked as not sent: open the offer when you have sent ${recipients.length - sentTo === 1 ? 'it' : 'them'}.` : undefined,
      actions: link,
    };
  } else {
    next = { tip: 'Nothing to do. It was withdrawn and must not be sent.', instead: 'If the market still needs one, draft a new offer from the supplier’s record.', actions: link };
  }

  return {
    subject: { kind: 'offer', title: o.title },
    summary: `A commercial offer for ${countryName(o.marketCode)} from ${o.supplierName}: ${plural(o.lineCount, 'line')}, ${OFFER_STATE_WORDS[o.state]}${o.validUntil ? `, valid until ${dayOf(o.validUntil)}` : ''}. Each line rests on something the supplier answered.${blocked > 0 ? ` ${plural(blocked, 'line is', 'lines are')} subject to confirmation.` : ''}`,
    need:
      o.state === 'draft'
        ? blocked > 0
          ? `The supplier’s written answer on: ${few(missing)}.`
          : 'A person to prepare it and then send it from their own address.'
        : o.state === 'prepared'
          ? 'A person to send it from their own address and mark it sent.'
          : 'Nothing.',
    why: 'An offer cannot be prepared while any line rests on a supplier answer that is not in writing, so it waits here until every line is confirmed.',
    source: { label: `${o.supplierName}’s record: each line names the answer it rests on`, to: `/suppliers/${o.supplierId}` },
    broughtBy: drafter,
    owner: o.state === 'sent' || o.state === 'withdrawn' ? undefined : lead,
    involved: o.draftedBy?.kind === 'agent' && drafter ? [drafter] : [],
    knows: who.knowers(ctx.department),
    facts: [
      ['Country', countryName(o.marketCode)],
      ['Lines subject to confirmation', blocked > 0 ? `${blocked} of ${o.lineCount}` : null],
      ['Terms', o.incoterms ?? null],
      ['Addressed to', recipients.length > 0 ? `${plural(recipients.length, 'buyer')}, ${sentTo} marked sent` : o.audience.length > 0 ? o.audience.join(', ') : null],
      ['Sent', o.sentAt ? when(o.sentAt) : null],
    ],
    next,
    ...(ctx.onPage ? {} : { open: { to: `/offers/${o.id}` } }),
  };
}
